import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import "./Login.css";

// Allowed email addresses
const ALLOWED_EMAILS = [
  'mail.trillionstockresearch@gmail.com',
  'vishsoni300@gmail.com'
];

// Utility functions for cookie management
const setCookie = (name, value, days = 7) => {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + days);
  
  document.cookie = `${name}=${value}; expires=${expirationDate.toUTCString()}; path=/; secure; samesite=strict`;
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // Get the previous location from state, default to '/dashboard'
  const from = location.state?.from?.pathname || '/dashboard';

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const checkAuthStatus = () => {
      // Check for auth token in cookies
      const authToken = getCookie('authToken');
      
      // Check for employee data in localStorage
      const employeeData = localStorage.getItem('employeeData');
      
      // Check for Firebase auth in sessionStorage
      const firebaseAuth = sessionStorage.getItem('firebaseAuth');
      const firebaseUser = sessionStorage.getItem('firebaseUser');
      
      // If any authentication method is present, redirect to dashboard
      if ((authToken && employeeData) || (firebaseAuth && firebaseUser)) {
        try {
          // Verify the employee data is valid JSON if it exists
          if (employeeData) {
            JSON.parse(employeeData);
          }
          
          // Redirect to previous location or dashboard
          navigate(from);
        } catch (error) {
          // If employee data is corrupted, clear it
          localStorage.removeItem('employeeData');
          deleteCookie('authToken');
        }
      }
    };

    checkAuthStatus();
  }, [navigate, from]);

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError("");
      setSuccess("");

      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if the email is in the allowed list
      if (!ALLOWED_EMAILS.includes(user.email)) {
        // Sign out the user immediately
        await signOut(auth);
        setError("Access denied. This email is not authorized to access the admin panel.");
        setIsLoading(false);
        return;
      }

      // Save user data to localStorage
      const userData = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
      };

      // Set cookie for authentication
      setCookie('authToken', user.accessToken || 'google-auth-token');
      localStorage.setItem('employeeData', JSON.stringify(userData));
      
      // Set Firebase authentication flag in sessionStorage
      sessionStorage.setItem('firebaseAuth', 'true');
      sessionStorage.setItem('firebaseUser', JSON.stringify(userData));

      setSuccess("Successfully signed in with Google!");
      
      // Redirect to previous location or dashboard
      setTimeout(() => {
        navigate(from);
      }, 1500);
    } catch (error) {
      console.error('Google sign-in error:', error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        setError("Sign-in popup was closed. Please try again.");
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError("Multiple popup attempts. Please wait a moment and try again.");
      } else {
        setError("Failed to sign in with Google. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tsr-login-container">
      <div className="tsr-login-form">
        <div className="tsr-login-header">
          <h1 className="tsr-login-brand">TrillionStock Research</h1>
          <h2 className="tsr-login-title">Welcome Back</h2>
          <p className="tsr-login-subtitle">Sign in with your Google account to continue</p>
        </div>

        {error && <div className="tsr-login-error">{error}</div>}
        {success && <div className="tsr-login-success">{success}</div>}
        
        <button 
          type="button"
          className="tsr-google-signin-btn"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
        >
          <svg className="tsr-google-icon" width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {isLoading ? "Signing in..." : "Continue with Google"}
        </button>
      </div>
    </div>
  );
}

export default Login;